<template>
  <div class="min-h-screen bg-off-black text-white">
    <div class="container mx-auto px-4 py-16 max-w-4xl">
      <!-- Header Section -->
      <div class="text-center mb-12">
        <h1 class="text-4xl md:text-5xl font-serif font-semibold mb-6 text-vibe-green">
          Smart Onboarding for Legal and Accounting Professionals
        </h1>
        <p class="text-xl md:text-2xl text-gray-green">
          Download your practical guide to AML compliance, risk management, and onboarding efficiency.
        </p>
      </div>

      <!-- Success Message -->
      <div v-if="submitted && !demoRequested" class="bg-green-600 border border-vibe-green rounded-lg p-8 mb-8 text-center">
        <h2 class="text-2xl font-semibold mb-4 text-vibe-green">Thank you!</h2>
        <p class="text-lg mb-4">Please check your email for the download link.</p>
        <p class="text-gray-green">The guide will be in your inbox shortly.</p>
      </div>

      <!-- Success Message with Demo -->
      <div v-if="submitted && demoRequested" class="bg-green-600 border border-vibe-green rounded-lg p-8 mb-8 text-center">
        <h2 class="text-2xl font-semibold mb-4 text-vibe-green">Thank you!</h2>
        <p class="text-lg mb-4">Please check your email for the download link.</p>
        <p class="text-gray-green">Our team will be in touch shortly about your demo request.</p>
      </div>

      <!-- Lead Capture Form -->
      <div v-if="!submitted" class="bg-green-700 rounded-lg p-8 md:p-12 shadow-2xl border border-green-600">
        <form @submit.prevent="handleSubmit" class="space-y-6">
          <!-- Full Name -->
          <div>
            <label for="fullName" class="block text-sm font-semibold mb-2 text-vibe-green">
              Full Name <span class="text-error-red">*</span>
            </label>
            <input
              type="text"
              id="fullName"
              v-model="formData.fullName"
              required
              class="w-full px-4 py-3 rounded-lg bg-green-800 border border-green-600 text-white placeholder-gray-green focus:outline-none focus:border-vibe-green transition-colors"
              placeholder="Enter your full name"
            />
          </div>

          <!-- Job Title -->
          <div>
            <label for="jobTitle" class="block text-sm font-semibold mb-2 text-vibe-green">
              Job Title <span class="text-error-red">*</span>
            </label>
            <input
              type="text"
              id="jobTitle"
              v-model="formData.jobTitle"
              required
              class="w-full px-4 py-3 rounded-lg bg-green-800 border border-green-600 text-white placeholder-gray-green focus:outline-none focus:border-vibe-green transition-colors"
              placeholder="Enter your job title"
            />
          </div>

          <!-- Company Name -->
          <div>
            <label for="companyName" class="block text-sm font-semibold mb-2 text-vibe-green">
              Company Name <span class="text-error-red">*</span>
            </label>
            <input
              type="text"
              id="companyName"
              v-model="formData.companyName"
              required
              class="w-full px-4 py-3 rounded-lg bg-green-800 border border-green-600 text-white placeholder-gray-green focus:outline-none focus:border-vibe-green transition-colors"
              placeholder="Enter your company name"
            />
          </div>

          <!-- Email Address -->
          <div>
            <label for="email" class="block text-sm font-semibold mb-2 text-vibe-green">
              Email Address <span class="text-error-red">*</span>
            </label>
            <input
              type="email"
              id="email"
              v-model="formData.email"
              required
              class="w-full px-4 py-3 rounded-lg bg-green-800 border border-green-600 text-white placeholder-gray-green focus:outline-none focus:border-vibe-green transition-colors"
              placeholder="Enter your email address"
            />
          </div>

          <!-- Sector Dropdown -->
          <div>
            <label for="sector" class="block text-sm font-semibold mb-2 text-vibe-green">
              Sector <span class="text-error-red">*</span>
            </label>
            <select
              id="sector"
              v-model="formData.sector"
              required
              class="w-full px-4 py-3 rounded-lg bg-green-800 border border-green-600 text-white focus:outline-none focus:border-vibe-green transition-colors"
            >
              <option value="" disabled>Select your sector</option>
              <option value="Legal">Legal</option>
              <option value="Accounting">Accounting</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <!-- Country -->
          <div>
            <label for="country" class="block text-sm font-semibold mb-2 text-vibe-green">
              Country <span class="text-error-red">*</span>
            </label>
            <input
              type="text"
              id="country"
              v-model="formData.country"
              required
              class="w-full px-4 py-3 rounded-lg bg-green-800 border border-green-600 text-white placeholder-gray-green focus:outline-none focus:border-vibe-green transition-colors"
              placeholder="Enter your country"
            />
          </div>

          <!-- Demo Checkbox -->
          <div class="flex items-start">
            <input
              type="checkbox"
              id="requestDemo"
              v-model="formData.requestDemo"
              class="mt-1 w-5 h-5 rounded border-green-600 bg-green-800 text-vibe-green focus:ring-vibe-green focus:ring-2"
            />
            <label for="requestDemo" class="ml-3 text-sm text-gray-green">
              I'm curious about the Marker AI Onboarding Platform - please tell me more.
            </label>
          </div>

          <!-- Error Message -->
          <div v-if="errorMessage" class="bg-error-red bg-opacity-20 border border-error-red rounded-lg p-4">
            <p class="text-error-red text-sm">{{ errorMessage }}</p>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            :disabled="isSubmitting"
            class="w-full btn-primary text-lg font-semibold py-4 transition-all duration-300 hover:bg-swamp-green hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ isSubmitting ? 'Submitting...' : 'Download the Guide' }}
          </button>

          <!-- Compliance Note -->
          <p class="text-xs text-gray-green text-center mt-6">
            We respect your privacy. Your details will be used to share relevant updates and onboarding insights. You can unsubscribe at any time.
          </p>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'SmartOnboardingDownload',
  data() {
    return {
      formData: {
        fullName: '',
        jobTitle: '',
        companyName: '',
        email: '',
        sector: '',
        country: '',
        requestDemo: false,
      },
      isSubmitting: false,
      submitted: false,
      demoRequested: false,
      errorMessage: '',
    };
  },
  methods: {
    async handleSubmit() {
      this.isSubmitting = true;
      this.errorMessage = '';

      try {
        // Submit form data to backend API
        const response = await axios.post('/api/submit-form', this.formData);

        if (response.data.success) {
          this.submitted = true;
          this.demoRequested = this.formData.requestDemo;
        }
      } catch (error) {
        console.error('Form submission error:', error);
        this.errorMessage = 'There was an error submitting your form. Please try again.';
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
/* Additional component-specific styles if needed */
</style>
